<template>
  <div class="usage-time-stats">
    <div class="page-header">
      <h2>使用时长统计</h2>
      <div class="header-controls">
        <div class="date-search">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            :shortcuts="dateShortcuts"
          />
          <el-button type="primary" :icon="Search" @click="handleSearch">搜索</el-button>
        </div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-cards">
      <el-row :gutter="20">
        <el-col :span="8">
          <el-card class="stat-card">
            <template #header>
              <div class="card-header">
                <span>平均使用时长</span>
                <el-icon><Timer /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ formatDuration(stats.averageDuration) }}</div>
              <div class="stat-label">日均使用时长</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="8">
          <el-card class="stat-card">
            <template #header>
              <div class="card-header">
                <span>最长使用时长</span>
                <el-icon><Timer /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ formatDuration(stats.maxDuration) }}</div>
              <div class="stat-label">单日最长使用时长</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="8">
          <el-card class="stat-card">
            <template #header>
              <div class="card-header">
                <span>总使用时长</span>
                <el-icon><Timer /></el-icon>
              </div>
            </template>
            <div class="card-content">
              <div class="stat-value">{{ formatDuration(stats.totalDuration) }}</div>
              <div class="stat-label">累计使用时长</div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 使用时长趋势图表 -->
    <div class="chart-container">
      <el-card class="chart-card">
        <template #header>
          <div class="card-header">
            <span>使用时长趋势</span>
          </div>
        </template>
        <div class="chart-content">
          <ChartBox
            :chart-data="durationChartData"
            :chart-options="durationChartOptions"
            height="400px"
          />
        </div>
      </el-card>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import ChartBox from '@/components/admin/ChartBox.vue'
import { Timer, Search } from '@element-plus/icons-vue'
import request from '@/utils/request'

// 日期范围选择
const dateRange = ref([])
const dateShortcuts = [
  {
    text: '最近一周',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
      return [start, end]
    }
  },
  {
    text: '最近一个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
      return [start, end]
    }
  },
  {
    text: '最近三个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
      return [start, end]
    }
  }
]

// 统计数据
const stats = ref({
  averageDuration: 0,
  maxDuration: 0,
  totalDuration: 0
})

// 图表数据
const durationChartData = ref({
  labels: [],
  datasets: [
    {
      label: '使用时长（分钟）',
      data: [],
      backgroundColor: 'rgba(64, 158, 255, 0.2)',
      borderColor: 'rgba(64, 158, 255, 1)',
      borderWidth: 2,
      tension: 0.4,
      fill: true
    }
  ]
})

const durationChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label: function(context) {
          return `使用时长: ${formatDuration(context.raw * 60)}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: function(value) {
          return formatDuration(value * 60) // 转换为秒
        },
        precision: 0
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  }
}

// 更新图表数据
const updateChartData = (trendData) => {
  console.log('更新图表数据：', trendData)
  if (!trendData || trendData.length === 0) {
    // 如果没有数据，清空图表
    durationChartData.value = {
      labels: [],
      datasets: [
        {
          label: '使用时长（分钟）',
          data: [],
          backgroundColor: 'rgba(64, 158, 255, 0.2)',
          borderColor: 'rgba(64, 158, 255, 1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    }
    return
  }

  // 格式化日期标签
  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString()
  }

  // 更新图表数据
  durationChartData.value = {
    labels: trendData.map(item => formatDate(item.usage_date)),
    datasets: [
      {
        label: '使用时长（分钟）',
        data: trendData.map(item => Math.round(item.day_total / 60)), // 转换为分钟并四舍五入
        backgroundColor: 'rgba(64, 158, 255, 0.2)',
        borderColor: 'rgba(64, 158, 255, 1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true
      }
    ]
  }

  // 更新图表配置
  const maxDuration = Math.max(...trendData.map(item => item.day_total / 60))
  durationChartOptions.scales.y.ticks.stepSize = Math.ceil(maxDuration / 5)
}

// 获取统计数据
const fetchStats = async () => {
  try {
    console.log('开始获取使用时长数据...')
    const response = await request.get('/admin/stats/usage-duration', {
      params: {
        startDate: dateRange.value[0],
        endDate: dateRange.value[1]
      }
    })
    console.log('获取到的数据：', response)
    
    if (!response || !response.trend) {
      throw new Error('返回数据格式不正确')
    }
    
    // 直接使用后端返回的统计数据
    stats.value = {
      averageDuration: response.avgDuration,
      maxDuration: response.maxDuration,
      totalDuration: response.totalDuration
    }
    
    // 更新图表数据
    updateChartData(response.trend)
    console.log('统计数据更新完成：', stats.value)
  } catch (error) {
    console.error('获取统计数据失败：', error)
    ElMessage.error(error.message || '获取统计数据失败')
  }
}

// 格式化时长
const formatDuration = (seconds) => {
  if (!seconds) return '0分钟'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0) {
    return `${hours}小时${minutes}分钟`
  }
  return `${minutes}分钟`
}

// 搜索处理
const handleSearch = () => {
  if (!dateRange.value || dateRange.value.length !== 2) {
    ElMessage.warning('请选择日期范围')
    return
  }
  fetchStats()
}

// 初始化
onMounted(() => {
  // 设置默认日期范围为最近一个月
  dateRange.value = dateShortcuts[1].value()
  // 初始化时加载数据
  fetchStats()
})
</script>

<style scoped>
.usage-time-stats {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.stats-cards {
  margin-bottom: 20px;
}

.stat-card {
  height: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-content {
  text-align: center;
  padding: 20px 0;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 8px;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

.chart-container {
  margin-bottom: 20px;
}

.chart-content {
  height: 400px;
}

.date-search {
  display: flex;
  gap: 12px;
  align-items: center;
}

.header-controls {
  display: flex;
  gap: 16px;
  align-items: center;
}
</style> 